import juice from 'juice';
import { ckEditorCss, TOKEN_URL } from '../config/common';
const API_URL_V2 = import.meta.env.VITE_GETSIGN_API_URL_V2;

export const isHebrew = (string) => {
  return !!string?.split('').some((x) => x.match(/[\u0590-\u05FF]/));
};

export const hasLatin = (string) => /[A-Za-z]/.test(string || '');

/**
 * BiDi direction for the *text* (not the chip block).
 * Pure Hebrew → rtl. Mixed → auto so first-strong matches monday.
 * Put on an inner isolate; keep toolbar Align on the outer via text-align.
 */
export const resolveTextDirection = (string) => {
  if (!string || !isHebrew(string)) return 'ltr';
  if (hasLatin(string)) return 'auto';
  return 'rtl';
};

/** Toolbar Align Left/Center/Right → CSS text-align (default left). */
export const resolveTextAlign = (alignment) => {
  if (alignment === 'center' || alignment === 'right') return alignment;
  return 'left';
};

/** Inner span style: BiDi isolate so dir=auto does not hug the chip to the right. */
export const BIDI_ISOLATE_STYLE = { unicodeBidi: 'isolate' };

export function copyToClipboard(text) {
  const htmlText =
    text === 'image'
      ? `<img src="https://www.w3schools.com/html/pic_trulli.jpg" width="640" height="480">`
      : `<span style="color: blue; font-size: 20px">{${text}}</span>`;
  if (navigator.clipboard && navigator.clipboard.write) {
    const type = 'text/html';
    const blob = new Blob([htmlText], { type });
    const data = [new ClipboardItem({ [type]: blob })];

    navigator.clipboard.write(data).then(
      function () {
        alert('Styled text copied to clipboard successfully!');
      },
      function (err) {
        console.error('Could not copy styled text: ', err);
      },
    );
  } else {
    console.error('Clipboard API not available.');
  }
}

const base64ToBlob = (base64, mimeType) => {
  // Remove data URL scheme if present
  if (base64.startsWith('data:')) {
    const base64Index = base64.indexOf('base64,') + 'base64,'.length;
    base64 = base64.substring(base64Index);
  }

  // Validate base64 string
  if (!/^([A-Za-z0-9+/=])+$/g.test(base64)) {
    throw new Error('Invalid base64 string');
  }

  // Decode the base64 string
  const byteCharacters = atob(base64);
  const byteArrays = [];

  // Convert binary string to byte array
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  // Create and return the Blob
  return new Blob(byteArrays, { type: mimeType });
};

const parseAndApplyInlineCSS = (res) => {
  const replacement = `<p class="page-break" style="page-break-after:always;"><span style="display:none;">&nbsp;</span></p>`;

  const parser = new DOMParser();
  const doc = parser.parseFromString(res.html, 'text/html');

  // Extract the CSS from the <style> tag and apply inline styles
  const styleTag = doc.querySelector('style');
  if (styleTag) {
    const css = styleTag.innerHTML;
    styleTag.remove(); // Remove the <style> tag from the DOM

    const styleSheet = new CSSStyleSheet();
    styleSheet.replaceSync(css);

    // Apply inline styles to matching elements
    const elements = doc.querySelectorAll('*');
    const rules = styleSheet.cssRules;
    for (let rule of rules) {
      if (rule.selectorText) {
        elements.forEach((element) => {
          if (element.matches(rule.selectorText)) {
            const cssText = rule.style.cssText;
            element.style.cssText += cssText; // Apply the inline styles
          }
        });
      }
    }
  }

  // Process <br> elements to replace with page-break elements
  // const brElements = doc.querySelectorAll('br');
  // brElements.forEach((br) => {
  //   if (!br.closest('table')) {
  //     const tempContainer = doc.createElement('div');
  //     tempContainer.innerHTML = replacement;
  //     const replacementElement = tempContainer.firstChild;

  //     br.replaceWith(replacementElement);
  //   }
  // });
  // Return the modified HTML content as a string
  return doc.body.innerHTML;
};

export const customConvertDocxToHtml = async (base64String, fileType) => {
  const sessionToken = localStorage.getItem('sessionToken');

  try {
    const response = await fetch(
      `${API_URL_V2}/file/convert-word-to-html?type=${fileType || 'docx'}`,
      {
        method: 'POST',
        body: JSON.stringify({ base64String }),
        headers: {
          'Content-Type': 'application/json',
          sessionToken: sessionToken,
        },
      },
    );
    const result = await response.json();
    const updatedResult = await parseAndApplyInlineCSS(result);

    return { html: updatedResult };
  } catch (error) {
    console.error('Conversion error:', error);
    throw error;
  }
};

export const convertDocxToHtml = async (base64String) => {
  const mimeType =
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  const blob = base64ToBlob(base64String, mimeType);
  const formData = new FormData();
  formData.append('file', blob, 'document.docx');

  const token = await fetch(TOKEN_URL).then((res) => res.text());

  try {
    const response = await fetch(
      'https://docx-converter.cke-cs.com/v2/convert/docx-html',
      {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: token,
        },
      },
    );
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Conversion error:', error);
    throw error;
  }
};

export function adjustColumnWidthsToPixels(html) {
  // Create a temporary DOM element to manipulate the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Select all tables within figure elements with class 'table'
  const tables = doc.querySelectorAll('figure.table');

  tables.forEach((table) => {
    // Initialize the default width
    let tableWidth = 658; // Default width if no width is found

    // Get the width of the current table (assuming it's set in pixels or percentage)
    const tableStyleWidth = table.style.width; // Get the table width as a string
    // Check if the table width is specified
    if (tableStyleWidth) {
      // Check if the width is in percentage
      if (tableStyleWidth.includes('%')) {
        const figureStyleWidth = tableWidth; // Get the figure width

        // If figure width is set, use that
        if (figureStyleWidth) {
          tableWidth =
            parseFloat(figureStyleWidth) * (parseFloat(tableStyleWidth) / 100);
        }
      } else {
        // Parse the width as a float in pixels
        tableWidth = parseFloat(tableStyleWidth) || 658; // Use default width if NaN
      }
    }

    // Select all <col> elements in the colgroup
    const cols = table.querySelectorAll('colgroup col');

    // Store column widths to apply to the corresponding td and tr later
    const columnWidths = [];

    // Iterate over each <col> and calculate its new pixel width based on percentage
    cols.forEach((col, index) => {
      const currentWidthPercentage = parseFloat(col.style.width); // Get the current width in percentage
      const newWidthInPixels = (currentWidthPercentage / 100) * tableWidth; // Convert percentage to pixels
      col.setAttribute('width', `${newWidthInPixels.toFixed(2)}`);
      columnWidths[index] = newWidthInPixels.toFixed(2); // Store the calculated pixel width
    });

    // Iterate over each <tr> and apply the widths to the corresponding <td> elements
    const rows = table.querySelectorAll('tbody tr');
    rows.forEach((row) => {
      let totalRowWidth = 0; // Initialize total row width
      const cells = row.querySelectorAll('td');

      // Iterate over the <td> elements and apply the corresponding width
      cells.forEach((cell, index) => {
        const colWidth = columnWidths[index]; // Get the corresponding column width
        if (colWidth) {
          cell.setAttribute('width', `${colWidth}`);
          totalRowWidth += parseFloat(colWidth); // Add up the total row width
        }
      });

      // Apply the total row width as the style to the <tr>
      row.setAttribute('width', `${totalRowWidth.toFixed(2)}`);
    });

    // Set width for the figure and table

    const tableInsideFigure = table.querySelector('table');
    table.setAttribute('width', `${tableWidth}`);
    tableInsideFigure.setAttribute('width', `${tableWidth}`);
  });

  // Serialize the document back to an HTML string
  const serializer = new XMLSerializer();
  return serializer.serializeToString(doc.body);
}

export function pageBreakTagModifier(htmlString) {
  // Replace <div class="page-break"> with <p>
  htmlString = htmlString.replace(
    /<div([^>]*class="page-break"[^>]*)>([\s\S]*?)<\/div>/g,
    '<p$1>$2</p>',
  );

  // Replace <mark> with <div>
  htmlString = htmlString
    .replace(/<mark([^>]*)>/g, '<span$1>')
    .replace(/<\/mark>/g, '</span>');

  return htmlString;
}

/**
 *
 * @param {Set} prevIds
 * @returns
 */
export const generateUniqueId = (prevIds) => {
  let id;
  do {
    id = Math.random().toString(36).substring(2, 6).toUpperCase();
  } while (prevIds.has(id));

  return id;
};

export function setDefaultFontAsArial(htmlString) {
  // Check if the HTML already has a default font set
  const hasDefaultFont =
    /<body[^>]*style="[^"]*font-family:[^"]*"/.test(htmlString) ||
    /<html[^>]*style="[^"]*font-family:[^"]*"/.test(htmlString);

  if (!hasDefaultFont) {
    // If body tag exists, add the font-family to it
    if (htmlString.includes('<body')) {
      htmlString = htmlString.replace(/<body([^>]*)>/, (match, attributes) => {
        // If the body already has a style attribute, add font-family to it
        if (attributes.includes('style="')) {
          return match.replace(
            /style="([^"]*)"/,
            'style="$1; font-family: Arial, sans-serif;"',
          );
        } else {
          // Otherwise, add a new style attribute
          return `<body${attributes} style="font-family: Arial, sans-serif;">`;
        }
      });
    } else {
      // If no body tag, wrap the content in a div with Arial font
      htmlString = `<div style="font-family: Arial, sans-serif;">${htmlString}</div>`;
    }
  }

  return htmlString;
}

export const beforeSaveHtmlParser = ({ html }) => {
  //add css file as inline css in html for libreoffice to parse
  const html1 = juice.inlineContent(html, ckEditorCss);

  //calculate the page break in doc file and add the page break tag
  const html2 = pageBreakTagModifier(html1);

  //calculate the width for each columns in tables in doc file and set each to pixel value
  const columnAdjustedHtml = adjustColumnWidthsToPixels(html2);

  //if no font provided as default font as arial
  const htmlContent = setDefaultFontAsArial(columnAdjustedHtml);

  return htmlContent;
};

export const applySubItemFilters = (subItems, filterConfig) => {
  if (!filterConfig || !filterConfig.isFilterEnabled) {
    return subItems;
  }
  const sanitizedFilters = (filterConfig.filters || [])
    .filter(Boolean)
    .filter((filter) => {
      if (!filter.targetColumnId) return false;
      if (['is', 'is not'].includes(filter.condition) && !filter.value)
        return false;
      return true;
    });

  if (sanitizedFilters.length === 0) {
    return subItems;
  }

  return subItems.filter((subItem) => {
    return sanitizedFilters.reduce((acc, filter) => {
      // Find the target column in the current item (subitem)
      const targetColumn = subItem[filter.targetColumnId];

      const condition = filter.condition;
      const values = filter.value; // `value` might me array as we might need to introduce multiple values for a single filter
      const targetValue = targetColumn; // The value of the target column

      let match = false;

      /*
      Tweak from this line to incoorporate filters for other column types rather than just 'Status'
      */

      // Handle conditions based on the value array
      if (Array.isArray(values)) {
        if (condition === 'is' && values.includes(targetValue)) {
          match = true;
        } else if (condition === 'is not' && !values.includes(targetValue)) {
          match = true;
        }
      } else {
        // Fallback to the single value logic if value is not an array (for backward compatibility)
        // TODO:: We can use variables for the conditions rather than hard coding.
        if (condition === 'is' && targetValue === values) {
          match = true;
        } else if (condition === 'is not' && targetValue !== values) {
          match = true;
        } else if (condition === 'is empty' && !Boolean(targetValue)) {
          match = true;
        } else if (
          condition === 'is not empty' &&
          targetValue !== '' &&
          targetValue != null
        ) {
          match = true;
        }
      }

      // Apply chaining logic: AND/OR
      if (filterConfig.filterChaining === 'AND') {
        return acc && match; // If chaining is AND, all conditions must match
      }

      return acc || match; // Handles for chaining OR or none.
    }, filterConfig.filterChaining === 'AND'); // Start with true for AND and false for OR
  });
};

export const filterGhostEmailColumns = (
  enveloperPlaceholers,
  envelopeSignerSettings,
  boardData,
) => {
  const placeholders = enveloperPlaceholers;

  const cleanedPlaceholders = placeholders.map((placeholder) => {
    const cleanedPlaceholder = {
      ...placeholder,
      fields: placeholder.fields.map((field) => ({
        ...field,
        signer:
          !!field?.signer?.userId ||
          !!boardData.columns.find(
            (c) =>
              !!field?.signer?.userId || c.id === field?.signer?.emailColumnId,
          )
            ? field?.signer
            : null,
      })),
      signers_settings: placeholder.signers_settings.filter((signer) => {
        return !!boardData.columns.find(
          (column) => !!signer.userId || column.id === signer.emailColumnId,
        );
      }),
    };

    return cleanedPlaceholder;
  });

  const cleanedSignerSettings = {
    ...envelopeSignerSettings,
    signers: envelopeSignerSettings.signers.filter((signer) => {
      return !!boardData.columns.find(
        (column) => !!signer.userId || column.id === signer.emailColumnId,
      );
    }),
  };

  delete cleanedSignerSettings.actions;

  return {
    cleanedPlaceholders,
    cleanedSignerSettings,
  };
};
